import { Button } from '@affine/component';
import { type FC, type ReactNode, useState } from 'react';

import * as styles from './settings-content.css';

export const SettingsContentSurface: FC<{ children: ReactNode }> = ({
  children,
}) => <section className={styles.surface}>{children}</section>;

export const SettingsTabbedContent: FC<{ children: ReactNode }> = ({
  children,
}) => <div className={styles.tabPanel}>{children}</div>;

export const SettingsContentSection: FC<{
  title?: ReactNode;
  children: ReactNode;
}> = ({ title, children }) => (
  <div className={styles.section}>
    {title ? <div className={styles.sectionTitle}>{title}</div> : null}
    {children}
  </div>
);

export const SettingsContentDivider: FC = () => (
  <div className={styles.divider} />
);

export const SettingsContentText: FC<{ children: ReactNode }> = ({
  children,
}) => <div className={styles.text}>{children}</div>;

export const SettingsCodeBlock: FC<{
  as?: 'code' | 'pre';
  children: ReactNode;
}> = ({ as = 'code', children }) =>
  as === 'pre' ? (
    <pre className={styles.codeBlock}>{children}</pre>
  ) : (
    <code className={styles.codeBlock}>{children}</code>
  );

export const SettingsActionRow: FC<{ children: ReactNode }> = ({
  children,
}) => <div className={styles.actionRow}>{children}</div>;

export const SettingsActionsSection: FC<{
  title: ReactNode;
  children: ReactNode;
}> = ({ title, children }) => (
  <div className={styles.actionsSection}>
    <div className={styles.divider} />
    <div className={styles.actionsHeader}>
      <div className={styles.actionsTitle}>{title}</div>
      <SettingsActionRow>{children}</SettingsActionRow>
    </div>
  </div>
);

export const SettingsDataList: FC<{ children: ReactNode }> = ({ children }) => (
  <div className={styles.dataList}>{children}</div>
);

export const SettingsDataRow: FC<{
  label: ReactNode;
  value: ReactNode;
}> = ({ label, value }) => (
  <div className={styles.dataRow}>
    <div className={styles.dataLabel}>{label}</div>
    <div className={styles.dataValue}>{value}</div>
  </div>
);

export const SettingsNotice: FC<{
  title: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
}> = ({ title, children, actions }) => (
  <div className={styles.notice}>
    <div className={styles.noticeTitle}>{title}</div>
    <div className={styles.noticeText}>{children}</div>
    {actions}
  </div>
);

export const SettingsConfirmButton: FC<{
  label: ReactNode;
  confirmLabel: ReactNode;
  cancelLabel: ReactNode;
  onConfirm: () => void;
  variant?: 'error' | 'secondary';
  disabled?: boolean;
  loading?: boolean;
  loadingLabel?: ReactNode;
  dataTestId?: string;
}> = ({
  label,
  confirmLabel,
  cancelLabel,
  onConfirm,
  variant = 'error',
  disabled,
  loading,
  loadingLabel,
  dataTestId,
}) => {
  const [confirm, setConfirm] = useState(false);

  if (confirm) {
    return (
      <>
        <Button
          variant={variant}
          onClick={onConfirm}
          disabled={disabled || loading}
          data-testid={dataTestId}
        >
          {loading ? (loadingLabel ?? confirmLabel) : confirmLabel}
        </Button>
        <Button variant="secondary" onClick={() => setConfirm(false)}>
          {cancelLabel}
        </Button>
      </>
    );
  }

  return (
    <Button
      variant={variant}
      onClick={() => setConfirm(true)}
      disabled={disabled}
      data-testid={dataTestId}
    >
      {label}
    </Button>
  );
};

// Fallback for a single failed query/mutation when the backend IS reachable
// (permission error, validation error, unexpected bug). When the backend is
// actually down, render the shared settings offline header action instead —
// checking
// `isBackendUnavailableError` first avoids showing both at once.
export const SettingsErrorBanner: FC<
  {
    message: ReactNode;
  } & (
    | { onRetry: () => void; retryLabel: ReactNode }
    | { onRetry?: undefined; retryLabel?: undefined }
  )
> = ({ message, onRetry, retryLabel }) => (
  <div className={styles.errorBanner}>
    <span className={styles.errorBannerText}>{message}</span>
    {onRetry ? (
      <Button variant="secondary" onClick={onRetry}>
        {retryLabel}
      </Button>
    ) : null}
  </div>
);
